import React, { FC, ReactNode } from 'react';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import styled from 'styled-components';
import {
  usePersonallyCraftedFLIPSource,
  usePersonallyCraftedFLIPTarget,
} from 'src/hooks/usePersonallyCraftedFLIP';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { makeTransition } from 'src/utils/motion/transitions';
import { useInView } from 'react-hook-inview';
import { MaterialPageTabs } from 'src/modules/materials/components/MaterialPage/MaterialPageTabs';
import { Skeleton } from 'src/components/common/Skeleton';
import { media } from 'src/styles/designs';
import { StyleProps } from '@summer/jst-react';
import { MaterialPageRelatedItems } from 'src/modules/materials/components/MaterialPage/MaterialPageRelatedItems';
import { MaterialPageTab } from 'src/modules/materials/components/MaterialPage/MaterialPageTabs/types';
import { NeauviaItem } from 'src/models/directus';

export interface MaterialPageTemplateProps<T extends NeauviaItem> {
  tabs: MaterialPageTab<T>[];
  item: T;
  children: ReactNode;
  sourceElement?: HTMLElement | null;
}

const HeaderSkeleton = styled.header`
  position: relative;
  width: 120vw;
  height: 592px;
  margin-left: -20vw;
`;

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;

  position: relative;
  overflow: hidden;
  z-index: 1;
`;

const ContentContainer = styled(ConstraintContainer)`
  display: grid;
  grid-template-areas: 'tabs' 'content' 'related';
  gap: 20px;
  padding: 8px 0;

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: 883px 360px;
    grid-template-rows: 50px auto;
    grid-template-areas: 'tabs related' 'content related';
    padding: 40px 0;
    gap: 45px 0;
  }
`;

const TabsContainer = styled(ConstraintContainer)`
  overflow: hidden;
  grid-area: tabs;
`;

const TabContentContainer = styled(ConstraintContainer)`
  grid-area: content;
  overflow: hidden;
`;

const RelatedSectionContainer = styled(ConstraintContainer)`
  grid-area: related;
`;

const TabContents = styled(motion.div)`
  display: grid;
  grid-auto-flow: row;
  grid-gap: 25px;
`;

export const MaterialPageTemplate = <T extends NeauviaItem>({
  tabs,
  children,
  sourceElement = null,
  item: untranslatedItem,
}: MaterialPageTemplateProps<T>) => {
  const [detailsRef, detailsIsVisible] = useInView({
    threshold: 0.2,
  });

  const selectedTab = useSelector(
    (s) => s.app.detailsPage.contentTab || tabs[0]?.id,
  );

  usePersonallyCraftedFLIPSource({
    source: sourceElement,
    id: 'videoFlip',
  });
  usePersonallyCraftedFLIPTarget({
    sourceId: 'videoThumbnail',
    target: sourceElement,
  });

  const item = useTranslateItem(untranslatedItem);

  return (
    <Container>
      <AnimatePresence initial={false}>{children}</AnimatePresence>

      <ContentContainer>
        <TabsContainer>
          <MaterialPageTabs
            selectedTab={selectedTab}
            detailsIsVisible={detailsIsVisible}
            tabs={tabs}
          />
        </TabsContainer>

        <TabContentContainer>
          <AnimatePresence exitBeforeEnter>
            {tabs.map(({ Renderer, rendererKey, id }) =>
              selectedTab === id ? (
                <TabContents
                  ref={detailsRef}
                  key={(rendererKey ?? item.id) + id}
                  {...makeTransition({
                    stagger: 0.08,
                  })}
                >
                  <Renderer item={item} />
                </TabContents>
              ) : null,
            )}
          </AnimatePresence>
        </TabContentContainer>

        <RelatedSectionContainer>
          <MaterialPageRelatedItems item={item} />
        </RelatedSectionContainer>
      </ContentContainer>
    </Container>
  );
};

export const MaterialPageLoader: FC<StyleProps> = ({ children, ...props }) => (
  <Container {...props}>
    <ConstraintContainer>
      {children ?? (
        <HeaderSkeleton>
          <Skeleton fill />
        </HeaderSkeleton>
      )}
      <TabContents style={{ display: 'flex', marginTop: 30 }}>
        <Skeleton
          width="30vw"
          height={50}
          style={{ maxWidth: '200px', margin: 0 }}
        />
        <Skeleton
          width="30vw"
          height={50}
          style={{ maxWidth: '200px', margin: 0 }}
        />
        <Skeleton
          width="30vw"
          height={50}
          style={{ maxWidth: '200px', margin: 0 }}
        />
      </TabContents>
    </ConstraintContainer>
  </Container>
);
